/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 *
 *  @author estelle.giuly
 *
 */

import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';

import {merge, Observable} from 'rxjs';
import {map, share, takeUntil} from 'rxjs/operators';

import {ClaimType} from '../../../../+ess/claims/claims.interfaces';
import {Destroyable} from '../../../../destroyable.component';
import {GroupZoneDescriptor, ZoneValues} from '../../../form-generator/form-generator.interfaces';
import {LocalizationService} from '../../../localization/localization.service';
import {TreeNode} from '../../../ui';
import {FormField, TaskDescriptionType, Variable} from '../../workflow-v2.interface';
import {FORM_PLACEHOLDER_TRANSLATE_PREFIX, FORM_TRANSLATE_PREFIX} from '../../workflow-v2.service';

@Component({
  selector: 'gp-workflow-form-element',
  templateUrl: './workflow-form-element.component.html',
  styleUrls: ['./workflow-form-element.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowFormElementComponent extends Destroyable implements OnInit {

  public readonly FORM_TRANSLATE_PREFIX = FORM_TRANSLATE_PREFIX;
  public readonly FORM_PLACEHOLDER_TRANSLATE_PREFIX = FORM_PLACEHOLDER_TRANSLATE_PREFIX;
  public readonly GP_EMPLOYEE_FORM_DESCRIPTOR_TASK = TaskDescriptionType.GP_EMPLOYEE_FORM_DESCRIPTOR_TASK;
  public readonly CLAIM_TASK = TaskDescriptionType.CLAIM_TASK;
  public readonly DEFAULT_TASK = TaskDescriptionType.DEFAULT_TASK;

  @Input()
  public field: FormField;

  @Input()
  public taskDefinitionKey: string;

  @Input()
  public descriptorLoading: boolean;

  @Output()
  private requestUpdate: EventEmitter<Variable> = new EventEmitter();

  @Output()
  public errorTriggered: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public loadingEnded: EventEmitter<boolean> = new EventEmitter<boolean>();

  public gpFieldIdPrefix: string = 'gp-';


  public trigger$: Observable<number>;

  public groupZoneDescriptors: GroupZoneDescriptor[] = [];

  public cliRegId: number;

  public contractId: number;

  public requestVariable: Variable;

  public groupValues: ZoneValues[] = [];

  public claimForm: UntypedFormGroup;
  public claimTypes: ClaimType[];
  public claimTypeTree: Array<TreeNode<any>>;
  public taskDescriptionType: TaskDescriptionType;
  public isTaskDescriptionField: boolean;


  constructor(
    private localization: LocalizationService) {
    super();

    this.trigger$ = merge(
      this.localization.changeDateFormatEvent,
      this.localization.changeLanguageEvent
    ).pipe(
      map(() => Math.random()),
      share(),
      takeUntil(this.componentDestroyed$)
    );
  }

  public ngOnInit() {
    this.isTaskDescriptionField = this.field.type === 'gp-task-description';
    if (this.isTaskDescriptionField) {
      this.onRequestUpdate(null);
    }
    this.initVariables();
  }


  private setTaskDescriptionType(): void {
    this.taskDescriptionType = !this.cliRegId || !this.requestVariable?.value || this.isExpensesRequest()
      ? TaskDescriptionType.DEFAULT_TASK
      : this.isClaimTypeTask()
        ? TaskDescriptionType.CLAIM_TASK
        : TaskDescriptionType.GP_EMPLOYEE_FORM_DESCRIPTOR_TASK;

    if (this.taskDescriptionType !== TaskDescriptionType.GP_EMPLOYEE_FORM_DESCRIPTOR_TASK && this.field.id !== 'comment') {
      this.loadingEnded.emit(true);
    }

  }

  public isClaimTypeTask(): boolean {
    return !!this.field.taskVariables.find(variable => variable.name === 'claimName');
  }

  public isExpensesRequest(): boolean {
    const type = this.field.taskVariables?.find(variable => variable.name === 'type');
    return type?.value === 'expensesRequest';
  }

  private initVariables() {
    this.field?.taskVariables?.forEach(variable => {
      switch (variable.name) {
        case 'cliRegId':
          this.cliRegId = variable.value;
          break;
        case 'contractId':
          this.contractId = variable.value;
          break;
        case 'request':
          this.requestVariable = variable;
          break;
        default:
          break;
      }
    });
    this.setTaskDescriptionType();
  }

  public onRequestUpdate(event: Variable) {
    this.requestUpdate.emit(event);
  }

  public emitError(event: string) {
    this.errorTriggered.emit(event);
  }

  public loadingEnd(event: boolean) {
    this.loadingEnded.emit(event);
  }

}
